import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApisDefinitionsService } from '@services/apis-definitions.service';
import { AccountingReportService } from '@services/accounting-report-service.service';
import { ButtonModule } from 'primeng/button';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { InputText } from 'primeng/inputtext';
import { Country, Port, ProviderInfo } from '@shared/interfaces/apis-definitions.interface';
import { Providers } from '@shared/interfaces/accounting-report.interface';

export type ApisDefinitionsDialogMode = 'add' | 'edit';

export interface ApisDefinitionsFormModel {
    id: number | null;
    countryCode: string;
    port: string;
    providerIds: number[];
    providerSupplier: string;
    active: boolean;
}

@Component({
    selector: 'app-apis-definitions-create-edit',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, Select, MultiSelect, ToggleSwitch, ButtonModule, InputText],
    templateUrl: './apis-definitions-create-edit.component.html'
})
export class ApisDefinitionsCreateEditComponent implements OnInit, OnChanges {
    @Input() mode: ApisDefinitionsDialogMode = 'add';
    @Input() data: Partial<ApisDefinitionsFormModel> | null = null;

    @Output() cancel = new EventEmitter<void>();
    @Output() save = new EventEmitter<ApisDefinitionsFormModel>();

    countries: Country[] = [];
    allPorts: Port[] = [];
    filteredPorts: Port[] = [];
    providers: Providers[] = [];
    submitted = false;
    loadingPorts = false;

    private fb = inject(FormBuilder);
    private apisService = inject(ApisDefinitionsService);
    private accountingReportService = inject(AccountingReportService);

    formGroup: FormGroup = this.fb.group({
        id: [null],
        countryCode: ['', Validators.required],
        port: ['', Validators.required],
        providerIds: [[]],
        providerSupplier: [''],
        active: [true]
    });

    ngOnInit(): void {
        this.loadAllPorts();

        this.formGroup.get('countryCode')?.valueChanges.subscribe((countryCode: string) => {
            if (countryCode) {
                this.filteredPorts = this.allPorts.filter((p) => p.country === countryCode);
            } else {
                this.filteredPorts = [];
            }
            this.formGroup.get('port')?.setValue('');
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            if (this.data) {
                const countryCode = this.data.countryCode ?? '';
                this.filteredPorts = countryCode ? this.allPorts.filter((p) => p.country === countryCode) : [];

                this.formGroup.patchValue({
                    id: this.data.id ?? null,
                    countryCode: countryCode,
                    port: this.data.port ?? '',
                    providerIds: this.data.providerIds ?? [],
                    providerSupplier: this.data.providerSupplier ?? '',
                    active: this.data.active ?? true
                });
            } else {
                this.formGroup.reset({
                    id: null,
                    countryCode: '',
                    port: '',
                    providerIds: [],
                    providerSupplier: '',
                    active: true
                });
                this.filteredPorts = [];
            }
            this.submitted = false;
        }
    }

    onCancel(): void {
        this.cancel.emit();
    }

    onSave(): void {
        this.submitted = true;
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        const v = this.formGroup.getRawValue() as any;
        const form: ApisDefinitionsFormModel = {
            id: v.id,
            countryCode: v.countryCode,
            port: v.port,
            providerIds: v.providerIds || [],
            providerSupplier: v.providerSupplier || '',
            active: !!v.active
        };

        this.save.emit(form);
    }

    private loadAllPorts(): void {
        this.loadingPorts = true;
        this.apisService.getAllPorts().subscribe({
            next: (res) => {
                this.allPorts = res || [];
                // Extract unique countries from ports
                const countryMap = new Map<string, Country>();
                this.allPorts.forEach((port) => {
                    if (!countryMap.has(port.country)) {
                        countryMap.set(port.country, {
                            code: port.country,
                            name: port.countryName
                        });
                    }
                });
                this.countries = Array.from(countryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
                this.loadingPorts = false;
            },
            error: () => {
                this.allPorts = [];
                this.countries = [];
                this.loadingPorts = false;
            }
        });

        this.accountingReportService.getProviders().subscribe({
            next: (res) => (this.providers = res || []),
            error: () => (this.providers = [])
        });
    }
}
